/**
 * Live Promote, in the panel beside the protocol: prepares the protocol on first open, then lists its scenarios
 * with their Ready and switches, opens one, reviews its tickets, and shows History. Every switch is a promotion
 * of the changed settings, confirmed first when it turns something on before it is Ready.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { reloadBackTestingTypes, useBackTestingTypes } from '@/hooks/use-back-testing-types';
import { usePermissions } from '@/hooks/use-permissions';
import { usePromotionPlan, usePromotionSettings, useStopPromotion } from '@/hooks/use-promotion';
import { invalidateQueries } from '@/lib/query-client';
import { getApiErrorMessage } from '@/lib/utils';
import { useBucketing } from '../use-bucketing';
import { liveKey, useLivePool } from '../use-live-pool';
import type { LivePrepareStage } from '../types';
import { HistoryView } from './history-view';
import { NotReadyDialog } from './not-ready-dialog';
import { Overview } from './overview';
import type { OverviewRow } from './view-rules';
import { PrepareView } from './prepare-view';
import { ReviewView } from './review-view';
import { ScenarioCard } from './scenario-card';
import { currentSettingsOf, isWritingReply, scenarioStateOf, withReply, withWrite } from './scenarios';
import { useLiveJob, usePrepare, usePromoteChange } from './use-live-promote';

type Screen = { kind: 'list' } | { kind: 'review'; typeId: string } | { kind: 'history' };

interface LivePromoteProps {
	sopId: string;
	/** The type a link names: it opens on that scenario. */
	initialTypeId?: string | null;
	/** The scenario open, so the protocol beside it can light its paths. */
	onTypeChange?: (typeId: string | null) => void;
}

export const LivePromote = ({ sopId, initialTypeId = null, onTypeChange }: LivePromoteProps) => {
	const { can } = usePermissions();
	const canEdit = can('edit', 'promoted-blueprint');
	const { data: view, isPending, error } = useLivePool(sopId);
	const { data: plan } = usePromotionPlan(sopId);
	const { data: settings } = usePromotionSettings();
	const { data: backTesting } = useBackTestingTypes(sopId);
	const { data: job } = useLiveJob(sopId);
	const prepare = usePrepare();
	const stop = useStopPromotion();
	const promote = usePromoteChange(sopId);
	const [screen, setScreen] = useState<Screen>({ kind: 'list' });
	const [openTypeId, setOpenTypeId] = useState<string | null>(initialTypeId);
	const [lastStage, setLastStage] = useState<LivePrepareStage | null>(null);
	useEffect(() => onTypeChange?.(openTypeId), [onTypeChange, openTypeId]);

	const bucketing = useBucketing(
		sopId,
		useCallback(
			(settled: boolean) => {
				if (!settled) return;
				invalidateQueries(liveKey(sopId));
				void reloadBackTestingTypes(sopId);
			},
			[sopId]
		)
	);

	const current = useMemo(() => (plan ? currentSettingsOf(plan) : {}), [plan]);
	const rows: OverviewRow[] = useMemo(
		() => (view?.types ?? []).map((type) => ({ type, state: scenarioStateOf(type, current[type.typeId]) })),
		[view, current]
	);
	const alerted = useMemo(
		() =>
			(plan?.promotion?.events ?? [])
				.filter((event) => event.kind === 'reject_alert_sent' && event.typeId && event.tool)
				.map((event) => `${event.typeId}:${event.tool}`),
		[plan]
	);

	const onPrepare = useCallback(async () => {
		try {
			const started = await prepare.mutateAsync({ sopId });
			setLastStage(started.stage);
		} catch (err) {
			toast.error(getApiErrorMessage(err, 'Could not prepare Promote'));
		}
	}, [prepare, sopId]);

	if (isPending) {
		return (
			<p className='flex items-center gap-2 p-4 text-xs text-gray-800'>
				<Loader2 className='size-3.5 animate-spin' aria-hidden /> Reading the live tickets…
			</p>
		);
	}
	if (error || !view) {
		return <p className='p-4 text-xs leading-4 text-gray-800'>{getApiErrorMessage(error, 'Could not read the live tickets')}</p>;
	}

	const busy = promote.busy || stop.isPending;
	const waitingDrafts = rows.flatMap((row) => (row.state === 'waiting' && row.type.draft ? [row.type.draft.draftId] : []));
	const minTickets = settings?.liveReadyMinTickets.value ?? null;
	const readyRule = settings
		? `${settings.liveReadyPercent.value}% or more of at least ${settings.liveReadyMinTickets.value} live tickets were Perfect or Good Enough`
		: 'enough of its live tickets were Perfect or Good Enough';
	const promotion = plan?.promotion ?? null;
	const on = promotion?.status === 'active';

	let body: React.ReactNode;
	if (view.stage === 'not_prepared' || !rows.length) {
		body = (
			<PrepareView
				view={view}
				run={bucketing.run}
				job={job}
				canEdit={canEdit}
				preparing={prepare.isPending}
				noLiveTickets={lastStage === 'no_live_tickets'}
				onPrepare={() => void onPrepare()}
			/>
		);
	} else if (screen.kind === 'history') {
		body = <HistoryView sopId={sopId} onBack={() => setScreen({ kind: 'list' })} />;
	} else if (screen.kind === 'review' && rows.some((row) => row.type.typeId === screen.typeId)) {
		const type = rows.find((row) => row.type.typeId === screen.typeId)!.type;
		body = <ReviewView sopId={sopId} type={type} onBack={() => setScreen({ kind: 'list' })} />;
	} else {
		body = (
			<Overview
				view={view}
				rows={rows}
				on={on}
				autoWrites={promotion?.status === 'active' ? promotion.autoWrites.length : 0}
				canEdit={canEdit}
				busy={busy}
				sorting={bucketing.running || bucketing.starting}
				readyRule={readyRule}
				onProtocolSwitch={(next) => {
					if (next) void promote.change(current);
					else
						stop.mutate(
							{ sopId },
							{ onError: (err) => toast.error(getApiErrorMessage(err, 'Could not stop the promotion')) }
						);
				}}
				onSort={() => void bucketing.start()}
				onPromoteAgain={() => void promote.change(current, waitingDrafts)}
				onHistory={() => setScreen({ kind: 'history' })}
				renderCard={(row) => (
					<ScenarioCard
						key={row.type.typeId}
						sopId={sopId}
						type={row.type}
						plan={plan?.types.find((type) => type.typeId === row.type.typeId)}
						branch={backTesting?.types.find((type) => type.id === row.type.typeId)?.branch}
						state={row.state}
						writingReply={isWritingReply(job, row.type.typeId)}
						open={openTypeId === row.type.typeId}
						minTickets={minTickets}
						promotedAt={on ? (promotion?.promotedAt ?? null) : null}
						alerted={alerted}
						canEdit={canEdit}
						busy={busy}
						onToggle={() => setOpenTypeId(openTypeId === row.type.typeId ? null : row.type.typeId)}
						onAutoSend={(next) => void promote.change(withReply(current, row.type.typeId, next ? 'auto' : 'draft'))}
						onPromote={() => row.type.draft && void promote.change(current, [row.type.draft.draftId])}
						onAutoApprove={(tool, next) =>
							void promote.change(withWrite(current, row.type.typeId, tool, next ? 'auto' : 'ask'))
						}
						onReview={() => setScreen({ kind: 'review', typeId: row.type.typeId })}
					/>
				)}
			/>
		);
	}

	return (
		<div className='flex min-h-0 flex-1 flex-col'>
			{body}
			<NotReadyDialog
				items={promote.pending}
				busy={promote.busy}
				onConfirm={() => void promote.confirm()}
				onCancel={promote.cancel}
			/>
		</div>
	);
};
